// ============================================================================
// CAMPAIGN LANDING PAGES — dedicated outbound pages for target tradie groups.
// Each page is shareable, focused and speaks to that group's real pain points.
// Edit copy here; every page reflects the change automatically.
// ============================================================================

export const campaigns = [
  {
    slug: "roof-restoration",
    audience: "Roof Restoration Businesses",
    navLabel: "Roof Restoration",
    eyebrow: "For Roof Restoration Tradies",
    heading: "The roofs you restore deserve to be seen.",
    subheading:
      "Help more local homeowners find your roof-restoration business, quote the right jobs and win the ones you deserve—without spending your evenings chasing marketing.",
    icon: "Home",
    accent: "#8DFE3F",
    heroImage: null,
    heroAlt:
      "Illustrated tradie hero for Roof Restoration",
    trustChips: [
      "Roof-restoration focused",
      "Local homeowner reach",
      "Quote follow-up built in",
      "Reviews & before/after credibility",
    ],
    painPoints: [
      {
        title: "Homeowners can't tell restoration from replacement",
        desc: "Most homeowners search 'roof repair' or 'new roof' and don't know a restoration is often the right answer. Your business gets skipped over.",
      },
      {
        title: "High-ticket quotes go cold",
        desc: "Roof restoration is a considered purchase. Homeowners get 2–3 quotes and think about it. Without disciplined follow-up, the win goes to whoever calls back.",
      },
      {
        title: "Your best work isn't visible online",
        desc: "The before-and-after photos on your phone tell the story better than any words—but they're not on your Google profile, website or social.",
      },
      {
        title: "Peaks and slumps run the business",
        desc: "Storms and seasons decide when you're flat out or waiting on quotes. Nothing steady is bringing in the right work in between.",
      },
      {
        title: "Reviews are inconsistent",
        desc: "You do great work, but you rarely ask for a review at the right moment—and competitors with less experience outrank you on Google.",
      },
    ],
    howWeHelp: [
      {
        title: "Rank locally for the right searches",
        desc: "We optimise your Google Business Profile and local presence around restoration-specific terms and your service suburbs.",
      },
      {
        title: "Turn your best jobs into proof",
        desc: "We help you capture and present before-and-after photos, testimonials and roof-type stories so homeowners trust you before they call.",
      },
      {
        title: "Campaigns matched to homeowner intent",
        desc: "Targeted Google and social campaigns for restoration, tile roofing, metal roofing and re-bedding—so enquiries land ready to talk.",
      },
      {
        title: "Structured quote follow-up",
        desc: "A written follow-up rhythm for every quote, so warm homeowners don't drift to another quote purely because someone else rang back first.",
      },
      {
        title: "Reviews on autopilot",
        desc: "A simple, respectful system that asks satisfied homeowners for a Google review at the moment they're happiest.",
      },
      {
        title: "Missed-call text-back",
        desc: "When you're up a ladder, a friendly text goes out automatically so the enquiry doesn't ring the next roofer.",
      },
    ],
    outcomes: [
      "More local homeowners finding you first",
      "Stronger, credible presence that wins the shortlist",
      "Higher quote-to-job conversion through better follow-up",
      "A steady stream of reviews building your reputation",
      "Less dependence on storms and seasons",
    ],
    faqs: [
      {
        q: "Do you only do social media?",
        a: "No. For roof restoration, the biggest levers are usually local visibility, Google search, credibility content and disciplined quote follow-up. Social plays a supporting role in trust and remarketing.",
      },
      {
        q: "Will you use my real jobs and photos?",
        a: "Only with your approval. We help you capture and present your genuine before-and-after work—no stock, no fake claims.",
      },
      {
        q: "Do I need a new website?",
        a: "Often no. The Growth Assessment identifies what can be improved or connected before recommending replacement.",
      },
    ],
  },

  {
    slug: "heating-cooling",
    audience: "Heating & Cooling Businesses",
    navLabel: "Heating & Cooling",
    eyebrow: "For Heating & Cooling Tradies",
    heading: "Every heatwave. Every cold snap. Ready to win the work.",
    subheading:
      "Build a heating and cooling business that captures peak-season demand, keeps maintenance customers coming back, and stops relying on the phone happening to ring at the right time.",
    icon: "Thermometer",
    accent: "#2CD59C",
    heroImage: null,
    heroAlt:
      "Illustrated tradie hero for Heating & Cooling",
    trustChips: [
      "Seasonal demand strategy",
      "Fast lead response",
      "Maintenance reactivation",
      "Local visibility & reviews",
    ],
    painPoints: [
      {
        title: "Demand spikes overwhelm the team",
        desc: "When the mercury moves, enquiries flood in—and half of them go unanswered because the team is already booked out.",
      },
      {
        title: "Repair versus replacement gets muddled",
        desc: "A homeowner calling about a broken split system needs a different response to someone weighing up a new ducted system—but both often get the same quote pack.",
      },
      {
        title: "Maintenance customers are forgotten",
        desc: "Filter cleans, servicing and health checks are steady revenue you could earn back easily—but there's no reminder system.",
      },
      {
        title: "Off-season is dead quiet",
        desc: "Winter into spring or summer into autumn, the phones go quiet and cashflow tightens. Nothing bridges the gap.",
      },
      {
        title: "Missed calls become lost jobs",
        desc: "A homeowner in 38°C heat won't wait. If you miss the call, they ring the next installer within minutes.",
      },
    ],
    howWeHelp: [
      {
        title: "Seasonally-timed campaigns",
        desc: "Campaigns switch on ahead of peak demand and adjust as the season moves—so you're visible when homeowners are searching.",
      },
      {
        title: "Split-purpose landing pages",
        desc: "Separate pages for install, replacement, repair and maintenance—so the right enquiry lands with the right context.",
      },
      {
        title: "Missed-call text-back",
        desc: "Every missed call triggers an instant friendly message keeping the homeowner engaged until you can call back.",
      },
      {
        title: "Maintenance reactivation",
        desc: "A compliant, gentle nudge to past customers before the season turns—filter cleans, health checks and upgrades.",
      },
      {
        title: "Off-season demand generation",
        desc: "Service, maintenance and pre-season campaigns keep the pipeline moving in the quieter months.",
      },
      {
        title: "Local trust and reviews",
        desc: "A stronger Google Business Profile, review generation and credibility content so more homeowners choose you first.",
      },
    ],
    outcomes: [
      "Captured demand during heatwaves and cold snaps",
      "A steadier off-season built on maintenance and service",
      "Faster response and higher call-to-job conversion",
      "Reactivated past customers returning for service and upgrades",
      "A calmer team, running a plan instead of reacting",
    ],
    faqs: [
      {
        q: "Can you contact our previous customers?",
        a: "Potentially, where contact data and proposed communication can be used appropriately. Reactivation follows applicable consent, privacy, identification and unsubscribe requirements.",
      },
      {
        q: "What if we're already flat out in summer?",
        a: "The focus shifts to off-season demand, maintenance revenue, and better qualification during peaks—so you win the jobs that actually suit your team.",
      },
      {
        q: "Do you guarantee lead volume?",
        a: "No. Results depend on market, offer, competition, capacity and response. We set clear measures, manage the agreed work and improve the system continually.",
      },
    ],
  },

  {
    slug: "solar-netcc-installers",
    audience: "Solar & Battery Installers (NETCC Approved Retailers)",
    navLabel: "Solar & Battery (NETCC)",
    eyebrow: "For NETCC Approved Solar & Battery Retailers",
    heading: "Your NETCC approval is a trust signal. Let's make sure the right customers see it.",
    subheading:
      "Turn your NETCC-approved status into a genuine competitive advantage—reach homeowners who value quality, run cleaner campaigns and follow up quotes through the longer sales cycle without letting anyone slip.",
    icon: "Sun",
    accent: "#8DFE3F",
    heroImage: null,
    heroAlt:
      "Illustrated tradie hero for Solar & Battery (NETCC)",
    trustChips: [
      "NETCC-aware messaging",
      "Quality-buyer targeting",
      "Structured quote follow-up",
      "Compliant reactivation",
    ],
    painPoints: [
      {
        title: "Homeowners can't tell approved retailers apart",
        desc: "Cheap operators dominate the ads. Your NETCC accreditation matters—but nothing on your site, profile or campaigns makes it obvious to a comparing homeowner.",
      },
      {
        title: "Longer sales cycles kill momentum",
        desc: "Solar and battery buyers take weeks to decide. Quotes go quiet, follow-up drops off, and the sale goes to whoever stayed in touch.",
      },
      {
        title: "Existing customers are an untapped upgrade market",
        desc: "Battery add-ons, expansion panels and system upgrades are sitting in your old customer list—but no one is reaching out compliantly.",
      },
      {
        title: "Lead quality is uneven",
        desc: "Paid leads from aggregators are shared, price-shopped and often unqualified—dragging down conversion and team morale.",
      },
      {
        title: "Rebate confusion muddies the message",
        desc: "State rebates, federal incentives, battery schemes—homeowners are lost, and your quote sits in a pile of misleading offers.",
      },
    ],
    howWeHelp: [
      {
        title: "Put NETCC front and centre",
        desc: "We build your NETCC approved retailer status into your website, Google profile, campaigns and follow-up so quality-minded homeowners see it clearly.",
      },
      {
        title: "Attract quality-first homeowners",
        desc: "Campaigns tuned to consideration-stage buyers who value approved retailers, quality equipment and warranty—not the cheapest wall-of-panels offer.",
      },
      {
        title: "Consultation booking, not just leads",
        desc: "Enquiries move to a booked consultation or site visit—so your best consultants speak to serious buyers instead of quoting cold contacts.",
      },
      {
        title: "Structured quote follow-up",
        desc: "A defined rhythm across the whole quote cycle—so warm buyers keep hearing from you instead of drifting to a discount operator.",
      },
      {
        title: "Compliant customer reactivation",
        desc: "Old enquiries and existing customers, contacted correctly for battery upgrades and system additions—within consent, privacy and unsubscribe requirements.",
      },
      {
        title: "Clear messaging around rebates",
        desc: "Simple, honest explanations of what applies to the homeowner in front of you—so trust is earned in the first conversation.",
      },
    ],
    outcomes: [
      "Higher-quality enquiries that value approval and warranty",
      "More consultations and site visits booked, not just leads collected",
      "Better quote conversion across a longer buying cycle",
      "Compliant reactivation of past enquiries and customers for battery upgrades",
      "A brand position that stands apart from the cheap end of the market",
    ],
    faqs: [
      {
        q: "Do you understand NETCC and the solar/battery sales cycle?",
        a: "Yes. We build the strategy around approved-retailer messaging, longer decision cycles and compliant reactivation—not generic lead-gen playbooks.",
      },
      {
        q: "Can you help with battery upgrades to existing customers?",
        a: "Potentially, yes—subject to consent, privacy, identification and unsubscribe requirements. We help structure the outreach and messaging compliantly.",
      },
      {
        q: "Is advertising spend included?",
        a: "No. Advertising media spend is separate unless expressly stated in a written proposal. This keeps your ad spend visible and adjustable.",
      },
    ],
  },
];

export const getCampaign = (slug) => campaigns.find((c) => c.slug === slug);
